import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';
import { Space, Text, Button } from '@cashfree-intl/coherent';

import { COMPLETED, PAYTM_DASHBOARD_LINK } from '../constants';
import validationSchema from '../validation';

import FormikComponent from '../../../components/Formik';
import FormInput from '../../../components/FormInput';

function PaytmForm({
  data,
  addPaytmTestConfig,
  addPaytmProdConfig,
  isEditMode,
  resetEditMode,
  closeModal,
  resetShowPaytmForm,
}) {
  const { paytmTestConfig, paytmProdConfig } = data;
  const { merchantPaytmId, merchantPaytmKey, stage } = paytmTestConfig;

  const initialValues = {
    merchantPaytmId:
      stage === COMPLETED ? paytmProdConfig.merchantPaytmId : merchantPaytmId,
    merchantPaytmKey:
      stage === COMPLETED ? paytmProdConfig.merchantPaytmKey : merchantPaytmKey,
    merchantPaytmWebsiteUrl: stage === COMPLETED ? 'DEFAULT' : 'WEBSTAGING',
    merchantPaytmChannelId: 'WEB',
    merchantPaytmIndustryType: 'Retail',
  };

  const onSubmit = (values, actions) => {
    if (stage !== COMPLETED) {
      if (isEditMode) {
        const payload = {
          id: paytmTestConfig.id,
          merchantPaytmId: values.merchantPaytmId,
          merchantPaytmKey: values.merchantPaytmKey,
        };
        addPaytmTestConfig({
          payload,
          actions,
          isEditMode,
          cbShowForm: resetShowPaytmForm,
          cbEditMode: resetEditMode,
        });
      } else
        addPaytmTestConfig({
          payload: values,
          actions,
          cbShowForm: resetShowPaytmForm,
        });
    } else {
      const payload = {
        merchantPaytmId: values.merchantPaytmId,
        merchantPaytmKey: values.merchantPaytmKey,
        merchantPaytmWebsiteUrl: values.merchantPaytmWebsiteUrl,
      };
      addPaytmProdConfig({
        payload,
        actions,
        cb: resetShowPaytmForm,
      });
    }
  };

  return (
    <FormikComponent
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        values = {},
        handleFieldBlur,
        handleFieldChange,
        handleSubmit,
        errors,
        touched,
        ...formikProps
      }) => {
        const disabledField =
          !formikProps.isValidating && formikProps.isSubmitting;

        return (
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <Space>
              <Space className="content-left" direction="column">
                <Text className="active" variant="b14" color="body" strong>
                  {stage === COMPLETED ? 'Production' : 'Test'} API Credentials
                </Text>
                <Text variant="b14" color="bodyLight" strong>
                  Test Transactions
                </Text>
              </Space>
              <Space direction="column">
                {stage === COMPLETED ? (
                  <Text className="description" variant="b14" color="bodyLight">
                    Your production API credentials are available in the
                    developer settings section of your{' '}
                    {
                      <a href={PAYTM_DASHBOARD_LINK} target="_blank">
                        Paytm dashboard
                      </a>
                    }
                    . Please enter them and proceed to do a transaction to
                    validate them.
                  </Text>
                ) : (
                  <Text className="description" variant="b14" color="bodyLight">
                    Enter your test API details below. They can be found in the
                    developer settings section of your PayTM dashboard, access
                    it by clicking{' '}
                    {
                      <a href={PAYTM_DASHBOARD_LINK} target="_blank">
                        here
                      </a>
                    }
                  </Text>
                )}
                <FormInput
                  fluid
                  id="merchantPaytmId"
                  name="merchantPaytmId"
                  label={`${
                    stage === COMPLETED ? 'Production' : 'Test'
                  } Merchant ID`}
                  value={values.merchantPaytmId ? values.merchantPaytmId : ''}
                  errors={errors}
                  touched={touched}
                  placeholder=""
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={disabledField}
                />
                <FormInput
                  fluid
                  id="merchantPaytmKey"
                  name="merchantPaytmKey"
                  label={`${
                    stage === COMPLETED ? 'Production' : 'Test'
                  } Merchant Key`}
                  value={values.merchantPaytmKey ? values.merchantPaytmKey : ''}
                  errors={errors}
                  touched={touched}
                  placeholder=""
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  disabled={disabledField}
                />
              </Space>
            </Space>
            <Space justifyContent="flex-end" className="cta-wrapper">
              <Button link onClick={closeModal} disabled={disabledField}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={disabledField}
                disabled={!formikProps.isValid || disabledField}
                primary
              >
                Next
              </Button>
            </Space>
          </Form>
        );
      }}
    </FormikComponent>
  );
}

PaytmForm.propTypes = {
  data: PropTypes.object.isRequired,
  addPaytmTestConfig: PropTypes.func.isRequired,
  addPaytmProdConfig: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  resetEditMode: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  resetShowPaytmForm: PropTypes.func.isRequired,
};

export default memo(PaytmForm);
